import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Target,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Pencil,
  Trash2,
  Calendar,
  DollarSign,
  Building2,
  MapPin,
  ArrowRight,
  TrendingUp,
  CheckCircle2,
  ChevronRight,
  Settings,
  Layers,
  GripVertical,
  LayoutGrid,
  List,
  Upload,
  Save,
  Loader2,
  BrainCircuit,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import OpportunityKanban from "../components/OpportunityKanban";
import CSVImportDialog from "../components/CSVImportDialog";
import PPAOpportunityIntelligence from "../components/PPAOpportunityIntelligence";
import ppaIntegrationService from "../services/ppaIntegrationService";

import { API_BASE_URL as API } from "../lib/backendUrl";

// Sortable Stage Item Component for drag-and-drop reordering
const SortableStageItem = ({ stage, index }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: stage.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 border border-border/50 rounded-lg bg-card ${
        isDragging ? "shadow-lg ring-2 ring-primary/50" : ""
      }`}
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded"
      >
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </div>
      <div
        className="w-4 h-4 rounded-full flex-shrink-0"
        style={{ backgroundColor: stage.color }}
      />
      <div className="flex-1">
        <span className="font-medium">{stage.name}</span>
        <span className="text-xs text-muted-foreground ml-2">
          {stage.probability}% probability
        </span>
      </div>
      {stage.is_closed && (
        <Badge variant="outline" className="text-xs">
          {stage.is_won ? "Won" : "Lost"}
        </Badge>
      )}
    </div>
  );
};

const OpportunitiesPage = () => {
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("kanban"); // "list" or "kanban"
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [filterStage, setFilterStage] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isPipelineSettingsOpen, setIsPipelineSettingsOpen] = useState(false);
  const [isCreatePipelineOpen, setIsCreatePipelineOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [form, setForm] = useState({});
  const [editingOpp, setEditingOpp] = useState(null);
  const [convertDialog, setConvertDialog] = useState({ open: false, opportunity: null });
  const [pipelineForm, setPipelineForm] = useState({ name: "", description: "", stages: [] });
  const [editedStages, setEditedStages] = useState([]);
  const [savingStages, setSavingStages] = useState(false);
  const [ppaHealth, setPpaHealth] = useState(null);
  const [ppaDialog, setPpaDialog] = useState({ open: false, opportunity: null });

  // DnD sensors for stage reordering
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchData();
    ppaIntegrationService.getHealth()
      .then((health) => setPpaHealth(health?.enabled ? health : null))
      .catch(() => setPpaHealth(null));
  }, []);

  useEffect(() => {
    if (selectedPipeline) {
      fetchOpportunities();
    }
  }, [selectedPipeline, filterStage]);

  // Sync edited stages when opening pipeline settings
  useEffect(() => {
    if (isPipelineSettingsOpen && selectedPipeline?.stages) {
      setEditedStages([...selectedPipeline.stages]);
    }
  }, [isPipelineSettingsOpen, selectedPipeline]);

  const fetchData = async () => {
    try {
      const [pipelineRes, compRes] = await Promise.all([
        axios.get(`${API}/pipelines`),
        axios.get(`${API}/companies?company_type=client`),
      ]);
      setPipelines(pipelineRes.data);
      setCompanies(compRes.data);
      
      // Select default pipeline or first one
      const defaultPipeline = pipelineRes.data.find(p => p.is_default) || pipelineRes.data[0];
      if (defaultPipeline) {
        setSelectedPipeline(defaultPipeline);
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchOpportunities = async () => {
    if (!selectedPipeline) return;
    try {
      const params = new URLSearchParams();
      params.append("pipeline_id", selectedPipeline.id);
      if (filterStage && filterStage !== "all") params.append("stage", filterStage);
      
      const res = await axios.get(`${API}/opportunities?${params}`);
      setOpportunities(res.data);
    } catch (error) {
      toast.error("Failed to load opportunities");
    }
  };

  // Handle drag end for stage reordering
  const handleStageDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      setEditedStages((stages) => {
        const oldIndex = stages.findIndex((s) => s.id === active.id);
        const newIndex = stages.findIndex((s) => s.id === over.id);
        const newStages = arrayMove(stages, oldIndex, newIndex);
        // Update order property
        return newStages.map((s, idx) => ({ ...s, order: idx + 1 }));
      });
    }
  };

  // Save reordered stages
  const handleSaveStageOrder = async () => {
    if (!selectedPipeline) return;
    setSavingStages(true);
    try {
      const res = await axios.put(`${API}/pipelines/${selectedPipeline.id}`, {
        stages: editedStages,
      });
      // Update local state
      const updatedPipeline = res.data;
      setPipelines(pipelines.map(p => p.id === updatedPipeline.id ? updatedPipeline : p));
      setSelectedPipeline(updatedPipeline);
      toast.success("Stage order saved");
    } catch (error) {
      toast.error("Failed to save stage order");
    } finally {
      setSavingStages(false);
    }
  };

  // Check if stages have been reordered
  const stagesChanged = () => {
    if (!selectedPipeline?.stages || !editedStages.length) return false;
    return JSON.stringify(selectedPipeline.stages.map(s => s.id)) !== JSON.stringify(editedStages.map(s => s.id));
  };

  const getEmptyForm = () => ({
    name: "",
    description: "",
    pipeline_id: selectedPipeline?.id || "",
    client_company_id: "none",
    estimated_value: "",
    city: "",
    state: "",
    due_date: "",
    expected_award_date: "",
    stage: selectedPipeline?.stages?.[0]?.id || "identified",
    probability_percent: selectedPipeline?.stages?.[0]?.probability || 0,
    source: "manual",
    notes: "",
  });

  const handleCreate = async () => {
    if (!form.name) {
      toast.error("Opportunity name is required");
      return;
    }
    try {
      const payload = {
        ...form,
        estimated_value: form.estimated_value ? parseFloat(form.estimated_value) : null,
        probability_percent: parseInt(form.probability_percent) || 0,
        client_company_id: form.client_company_id && form.client_company_id !== "none" ? form.client_company_id : null,
        pipeline_id: selectedPipeline?.id,
      };
      await axios.post(`${API}/opportunities`, payload);
      toast.success("Opportunity created");
      setIsCreateOpen(false);
      setForm(getEmptyForm());
      fetchOpportunities();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create opportunity");
    }
  };

  const handleUpdate = async () => {
    if (!editingOpp) return;
    try {
      const payload = {
        ...form,
        estimated_value: form.estimated_value ? parseFloat(form.estimated_value) : null,
        probability_percent: parseInt(form.probability_percent) || 0,
        client_company_id: form.client_company_id && form.client_company_id !== "none" ? form.client_company_id : null,
      };
      await axios.put(`${API}/opportunities/${editingOpp.id}`, payload);
      toast.success("Opportunity updated");
      setIsEditOpen(false);
      setEditingOpp(null);
      setForm(getEmptyForm());
      fetchOpportunities();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update opportunity");
    }
  };

  const handleConvert = async () => {
    if (!convertDialog.opportunity) return;
    try {
      const res = await axios.post(`${API}/opportunities/${convertDialog.opportunity.id}/convert`, {
        decision_reason: "Converted from opportunities pipeline"
      });
      toast.success(`Created project: ${res.data.project_name}`);
      setConvertDialog({ open: false, opportunity: null });
      navigate(`/projects/${res.data.project_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to convert opportunity");
    }
  };

  const handleArchive = async (opp) => {
    try {
      await axios.delete(`${API}/opportunities/${opp.id}`);
      toast.success("Opportunity archived");
      fetchOpportunities();
    } catch (error) {
      toast.error("Failed to archive opportunity");
    }
  };

  const handleMovePipeline = async (opp, newPipelineId) => {
    try {
      const newPipeline = pipelines.find(p => p.id === newPipelineId);
      const firstStage = newPipeline?.stages?.[0]?.id || "identified";
      
      await axios.put(`${API}/opportunities/${opp.id}`, {
        pipeline_id: newPipelineId,
        stage: firstStage,
      });
      toast.success(`Moved to ${newPipeline?.name}`);
      fetchOpportunities();
    } catch (error) {
      toast.error("Failed to move opportunity");
    }
  };

  const handleStageChange = async (opp, newStage, newProbability) => {
    try {
      await axios.put(`${API}/opportunities/${opp.id}`, {
        stage: newStage,
        probability_percent: newProbability,
      });
      toast.success(`Moved to ${getStageLabel(newStage)}`);
      fetchOpportunities();
    } catch (error) {
      toast.error("Failed to update stage");
    }
  };

  const openEdit = (opp) => {
    setEditingOpp(opp);
    setForm({
      name: opp.name || "",
      description: opp.description || "",
      pipeline_id: opp.pipeline_id || "",
      client_company_id: opp.client_company_id || "none",
      estimated_value: opp.estimated_value?.toString() || "",
      city: opp.city || "",
      state: opp.state || "",
      due_date: opp.due_date || "",
      expected_award_date: opp.expected_award_date || "",
      stage: opp.stage || "identified",
      probability_percent: opp.probability_percent || 0,
      source: opp.source || "manual",
      notes: opp.notes || "",
    });
    setIsEditOpen(true);
  };

  const getStageColor = (stageId) => {
    const stage = selectedPipeline?.stages?.find(s => s.id === stageId);
    return stage?.color || "#64748b";
  };

  const getStageLabel = (stageId) => {
    const stage = selectedPipeline?.stages?.find(s => s.id === stageId);
    return stage?.name || stageId;
  };

  const formatCurrency = (value) => {
    if (!value) return "-";
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  };

  const filteredOpportunities = opportunities.filter(o =>
    o.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    o.client_company_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate pipeline stats
  const activeStages = selectedPipeline?.stages?.filter(s => !s.is_closed) || [];
  const pipelineValue = filteredOpportunities
    .filter(o => !selectedPipeline?.stages?.find(s => s.id === o.stage)?.is_closed)
    .reduce((sum, o) => sum + (o.estimated_value || 0), 0);
  
  const weightedValue = filteredOpportunities
    .filter(o => !selectedPipeline?.stages?.find(s => s.id === o.stage)?.is_closed)
    .reduce((sum, o) => sum + ((o.estimated_value || 0) * (o.probability_percent || 0) / 100), 0);

  // Pipeline Settings Handlers
  const handleCreatePipeline = async () => {
    if (!pipelineForm.name) {
      toast.error("Pipeline name is required");
      return;
    }
    try {
      const res = await axios.post(`${API}/pipelines`, {
        name: pipelineForm.name,
        description: pipelineForm.description,
        stages: pipelineForm.stages.length > 0 ? pipelineForm.stages : null,
      });
      toast.success("Pipeline created");
      setIsCreatePipelineOpen(false);
      setPipelineForm({ name: "", description: "", stages: [] });
      fetchData();
      setSelectedPipeline(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create pipeline");
    }
  };

  const handleUpdatePipelineStages = async (stages) => {
    if (!selectedPipeline) return;
    try {
      await axios.put(`${API}/pipelines/${selectedPipeline.id}`, { stages });
      toast.success("Stages updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update stages");
    }
  };

  const OpportunityForm = ({ onSubmit, submitLabel }) => (
    <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
      <div className="space-y-2">
        <Label>Opportunity Name *</Label>
        <Input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Downtown Office Renovation"
          className="bg-muted/50"
        />
      </div>

      <div className="space-y-2">
        <Label>Description</Label>
        <Textarea
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Project scope and details..."
          className="bg-muted/50"
          rows={3}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Client</Label>
          <Select value={form.client_company_id} onValueChange={(v) => setForm({ ...form, client_company_id: v })}>
            <SelectTrigger className="bg-muted/50">
              <SelectValue placeholder="Select client..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No Client</SelectItem>
              {companies.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Estimated Value</Label>
          <Input
            type="number"
            value={form.estimated_value}
            onChange={(e) => setForm({ ...form, estimated_value: e.target.value })}
            placeholder="1000000"
            className="bg-muted/50"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>City</Label>
          <Input
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
            className="bg-muted/50"
          />
        </div>
        <div className="space-y-2">
          <Label>State</Label>
          <Input
            value={form.state}
            onChange={(e) => setForm({ ...form, state: e.target.value })}
            className="bg-muted/50"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Due Date</Label>
          <Input
            type="date"
            value={form.due_date}
            onChange={(e) => setForm({ ...form, due_date: e.target.value })}
            className="bg-muted/50"
          />
        </div>
        <div className="space-y-2">
          <Label>Expected Award Date</Label>
          <Input
            type="date"
            value={form.expected_award_date}
            onChange={(e) => setForm({ ...form, expected_award_date: e.target.value })}
            className="bg-muted/50"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Stage</Label>
          <Select value={form.stage} onValueChange={(v) => {
            const stage = selectedPipeline?.stages?.find(s => s.id === v);
            setForm({ ...form, stage: v, probability_percent: stage?.probability || form.probability_percent });
          }}>
            <SelectTrigger className="bg-muted/50">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {selectedPipeline?.stages?.filter(s => !s.is_closed).map(s => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Probability %</Label>
          <Input
            type="number"
            min="0"
            max="100"
            value={form.probability_percent}
            onChange={(e) => setForm({ ...form, probability_percent: e.target.value })}
            className="bg-muted/50"
          />
        </div>
        <div className="space-y-2">
          <Label>Source</Label>
          <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
            <SelectTrigger className="bg-muted/50">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="manual">Manual</SelectItem>
              <SelectItem value="sam">SAM.gov</SelectItem>
              <SelectItem value="state">State Portal</SelectItem>
              <SelectItem value="referral">Referral</SelectItem>
              <SelectItem value="website">Website</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          placeholder="Additional notes..."
          className="bg-muted/50"
          rows={2}
        />
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={() => { setIsCreateOpen(false); setIsEditOpen(false); }}>
          Cancel
        </Button>
        <Button onClick={onSubmit} className="bg-primary">
          {submitLabel}
        </Button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header with Pipeline Selector */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold font-heading">Opportunities</h1>
            <p className="text-muted-foreground">Track and manage your sales pipeline</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select 
            value={selectedPipeline?.id || ""} 
            onValueChange={(v) => {
              const pipeline = pipelines.find(p => p.id === v);
              setSelectedPipeline(pipeline);
              setFilterStage("");
            }}
          >
            <SelectTrigger className="w-[200px] bg-muted/50" data-testid="pipeline-selector">
              <Layers className="mr-2 h-4 w-4" />
              <SelectValue placeholder="Select Pipeline" />
            </SelectTrigger>
            <SelectContent>
              {pipelines.map(p => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name} ({p.opportunity_count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => setIsPipelineSettingsOpen(true)}
            data-testid="pipeline-settings-btn"
          >
            <Settings className="h-4 w-4" />
          </Button>

          <Button 
            variant="outline" 
            onClick={() => setIsImportOpen(true)}
            data-testid="import-csv-btn"
          >
            <Upload className="mr-2 h-4 w-4" />
            Import CSV
          </Button>

          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary" onClick={() => setForm(getEmptyForm())} data-testid="add-opportunity-btn">
                <Plus className="mr-2 h-4 w-4" />
                Add Opportunity
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card max-w-2xl">
              <DialogHeader>
                <DialogTitle>Add Opportunity to {selectedPipeline?.name}</DialogTitle>
              </DialogHeader>
              <OpportunityForm onSubmit={handleCreate} submitLabel="Create Opportunity" />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* CSV Import Dialog */}
      <CSVImportDialog
        open={isImportOpen}
        onOpenChange={setIsImportOpen}
        pipelineId={selectedPipeline?.id}
        onImportComplete={fetchOpportunities}
      />

      {/* Pipeline Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="stat-card">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pipeline Value</p>
                <p className="text-2xl font-bold">{formatCurrency(pipelineValue)}</p>
              </div>
              <DollarSign className="h-8 w-8 text-primary/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Weighted Value</p>
                <p className="text-2xl font-bold">{formatCurrency(weightedValue)}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Opportunities</p>
                <p className="text-2xl font-bold">
                  {filteredOpportunities.filter(o => !selectedPipeline?.stages?.find(s => s.id === o.stage)?.is_closed).length}
                </p>
              </div>
              <Target className="h-8 w-8 text-blue-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Won</p>
                <p className="text-2xl font-bold">
                  {filteredOpportunities.filter(o => selectedPipeline?.stages?.find(s => s.id === o.stage)?.is_won).length}
                </p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-500/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search opportunities..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-muted/50"
            data-testid="opportunity-search"
          />
        </div>
        <Select value={filterStage || "all"} onValueChange={(v) => setFilterStage(v === "all" ? "" : v)}>
          <SelectTrigger className="w-48 bg-muted/50" data-testid="stage-filter">
            <Filter className="mr-2 h-4 w-4" />
            <SelectValue placeholder="All Stages" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            {selectedPipeline?.stages?.map(s => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* View Toggle */}
        <div className="flex border border-border rounded-lg overflow-hidden">
          <Button
            variant={viewMode === "kanban" ? "default" : "ghost"}
            size="sm"
            className={`rounded-none px-3 ${viewMode === "kanban" ? "bg-primary" : ""}`}
            onClick={() => setViewMode("kanban")}
            data-testid="kanban-view-btn"
          >
            <LayoutGrid className="h-4 w-4 mr-1" />
            Kanban
          </Button>
          <Button
            variant={viewMode === "list" ? "default" : "ghost"}
            size="sm"
            className={`rounded-none px-3 ${viewMode === "list" ? "bg-primary" : ""}`}
            onClick={() => setViewMode("list")}
            data-testid="list-view-btn"
          >
            <List className="h-4 w-4 mr-1" />
            List
          </Button>
        </div>
      </div>

      {/* Stage Pills - only show in list view */}
      {viewMode === "list" && (
        <div className="flex gap-2 flex-wrap">
          {selectedPipeline?.stages?.filter(s => !s.is_closed).map(stage => {
            const count = opportunities.filter(o => o.stage === stage.id).length;
            const isActive = filterStage === stage.id;
            return (
              <Badge
                key={stage.id}
                variant="outline"
                className="cursor-pointer transition-all"
                style={{
                  backgroundColor: isActive ? `${stage.color}20` : undefined,
                  color: isActive ? stage.color : undefined,
                  borderColor: isActive ? `${stage.color}50` : undefined,
                }}
                onClick={() => setFilterStage(isActive ? "" : stage.id)}
              >
                {stage.name} ({count})
              </Badge>
            );
          })}
        </div>
      )}

      {/* Kanban View */}
      {viewMode === "kanban" && (
        <OpportunityKanban
          opportunities={filteredOpportunities}
          pipeline={selectedPipeline}
          onStageChange={handleStageChange}
          onEdit={openEdit}
          onArchive={handleArchive}
          onConvert={(opp) => setConvertDialog({ open: true, opportunity: opp })}
          onPPA={ppaHealth?.enabled ? (opp) => setPpaDialog({ open: true, opportunity: opp }) : null}
        />
      )}

      {/* List View - Opportunities Table */}
      {viewMode === "list" && (
        <div className="rounded-lg border border-border/50 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                <TableHead>Opportunity</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Stage</TableHead>
                <TableHead>Value</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOpportunities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <Target className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No opportunities found</p>
                    <Button className="mt-4" onClick={() => { setForm(getEmptyForm()); setIsCreateOpen(true); }}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add Opportunity
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                filteredOpportunities.map((opp) => (
                  <TableRow key={opp.id} className="hover:bg-muted/20" data-testid={`opportunity-row-${opp.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Target className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <span className="font-medium">{opp.name}</span>
                          {opp.converted_project_id && (
                            <Badge variant="outline" className="ml-2 bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                              Converted
                            </Badge>
                          )}
                          {opp.city && opp.state && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                              <MapPin className="h-3 w-3" />
                              {opp.city}, {opp.state}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {opp.client_company_name ? (
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <Building2 className="h-3 w-3" />
                          {opp.client_company_name}
                        </span>
                      ) : "-"}
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant="outline" 
                        style={{
                          backgroundColor: `${getStageColor(opp.stage)}20`,
                          color: getStageColor(opp.stage),
                          borderColor: `${getStageColor(opp.stage)}50`,
                        }}
                      >
                        {getStageLabel(opp.stage)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{formatCurrency(opp.estimated_value)}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-muted-foreground">{opp.probability_percent}%</span>
                          <Progress value={opp.probability_percent} className="w-12 h-1.5" />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {opp.due_date ? (
                        <span className="flex items-center gap-1 text-muted-foreground text-sm">
                          <Calendar className="h-3 w-3" />
                          {new Date(opp.due_date).toLocaleDateString()}
                        </span>
                      ) : "-"}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(opp)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          {ppaHealth?.enabled && (
                            <DropdownMenuItem onClick={() => setPpaDialog({ open: true, opportunity: opp })}>
                              <BrainCircuit className="mr-2 h-4 w-4" />
                              PPA Intelligence
                            </DropdownMenuItem>
                          )}
                          {pipelines.length > 1 && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem disabled className="text-xs text-muted-foreground">
                                Move to Pipeline
                              </DropdownMenuItem>
                              {pipelines.filter(p => p.id !== selectedPipeline?.id).map(p => (
                                <DropdownMenuItem key={p.id} onClick={() => handleMovePipeline(opp, p.id)}>
                                  <ArrowRight className="mr-2 h-4 w-4" />
                                  {p.name}
                                </DropdownMenuItem>
                              ))}
                            </>
                          )}
                          {!opp.converted_project_id && !selectedPipeline?.stages?.find(s => s.id === opp.stage)?.is_closed && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => setConvertDialog({ open: true, opportunity: opp })}
                                className="text-green-400"
                              >
                                <ArrowRight className="mr-2 h-4 w-4" />
                                Convert to Project
                              </DropdownMenuItem>
                            </>
                          )}
                          {opp.converted_project_id && (
                            <DropdownMenuItem onClick={() => navigate(`/projects/${opp.converted_project_id}`)}>
                              <ChevronRight className="mr-2 h-4 w-4" />
                              View Project
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleArchive(opp)} className="text-destructive">
                            <Trash2 className="mr-2 h-4 w-4" />
                            Archive
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Edit Dialog */}
      {ppaHealth?.enabled && (
        <PPAOpportunityIntelligence
          opportunity={ppaDialog.opportunity}
          health={ppaHealth}
          open={ppaDialog.open}
          onOpenChange={(open) => setPpaDialog((current) => ({ ...current, open }))}
        />
      )}

      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="bg-card max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Opportunity</DialogTitle>
          </DialogHeader>
          <OpportunityForm onSubmit={handleUpdate} submitLabel="Save Changes" />
        </DialogContent>
      </Dialog>

      {/* Convert Dialog */}
      <AlertDialog open={convertDialog.open} onOpenChange={(open) => setConvertDialog({ ...convertDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Convert to Project</AlertDialogTitle>
            <AlertDialogDescription>
              This will create a new project from "{convertDialog.opportunity?.name}" and mark this opportunity as won.
              The opportunity will remain as a historical record linked to the new project.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConvert} className="bg-primary">
              Convert to Project
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Pipeline Settings Dialog */}
      <Dialog open={isPipelineSettingsOpen} onOpenChange={setIsPipelineSettingsOpen}>
        <DialogContent className="bg-card max-w-2xl">
          <DialogHeader>
            <DialogTitle>Pipeline Settings: {selectedPipeline?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Create New Pipeline */}
            <div className="flex items-center justify-between p-4 border border-border/50 rounded-lg">
              <div>
                <h3 className="font-medium">Create New Pipeline</h3>
                <p className="text-sm text-muted-foreground">Add another pipeline for different opportunity types</p>
              </div>
              <Button onClick={() => { setIsCreatePipelineOpen(true); setIsPipelineSettingsOpen(false); }}>
                <Plus className="mr-2 h-4 w-4" />
                New Pipeline
              </Button>
            </div>

            {/* Stage Configuration with Drag-and-Drop */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium">Pipeline Stages</h3>
                {stagesChanged() && (
                  <Button 
                    size="sm" 
                    onClick={handleSaveStageOrder}
                    disabled={savingStages}
                  >
                    {savingStages ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="mr-2 h-4 w-4" />
                    )}
                    Save Order
                  </Button>
                )}
              </div>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleStageDragEnd}
              >
                <SortableContext
                  items={editedStages.map(s => s.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {editedStages.map((stage, index) => (
                      <SortableStageItem key={stage.id} stage={stage} index={index} />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
              <p className="text-xs text-muted-foreground mt-3">
                Drag stages to reorder. Changes affect how stages appear on the Kanban board.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Pipeline Dialog */}
      <Dialog open={isCreatePipelineOpen} onOpenChange={setIsCreatePipelineOpen}>
        <DialogContent className="bg-card max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Pipeline</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Pipeline Name *</Label>
              <Input
                value={pipelineForm.name}
                onChange={(e) => setPipelineForm({ ...pipelineForm, name: e.target.value })}
                placeholder="e.g., Government Contracts"
                className="bg-muted/50"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={pipelineForm.description}
                onChange={(e) => setPipelineForm({ ...pipelineForm, description: e.target.value })}
                placeholder="What type of opportunities go in this pipeline?"
                className="bg-muted/50"
                rows={2}
              />
            </div>
            <p className="text-sm text-muted-foreground">
              The new pipeline will start with default stages. You can customize them after creation.
            </p>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setIsCreatePipelineOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreatePipeline} className="bg-primary">
                Create Pipeline
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OpportunitiesPage;
